import { applyCanvasFont } from "../layout/measure";
import { normalizeSearchText } from "../../creditSearch";

const FOCUS_RADIUS = 12;
const SEARCH_HIT_BG = "#ffe14a";
const SEARCH_HIT_FG = "#1a1028";
const WATERMARK_OPACITY = 0.2;

const supportsLetterSpacing = (ctx) => "letterSpacing" in ctx;

const isInvisible = (color) => !color || color === "transparent";

const intersects = (top, bottom, yFrom, yTo) => bottom >= yFrom && top <= yTo;

const fontMetrics = (ctx, op) => {
  const m = ctx.measureText("Hg");
  return {
    ascent: m.fontBoundingBoxAscent ?? op.font.size * 0.8,
    descent: m.fontBoundingBoxDescent ?? op.font.size * 0.2,
  };
};

const fillTextSpaced = (ctx, text, x, y, letterSpacing) => {
  if (!letterSpacing || supportsLetterSpacing(ctx)) {
    if (supportsLetterSpacing(ctx)) ctx.letterSpacing = `${letterSpacing || 0}px`;
    ctx.fillText(text, x, y);
    return;
  }
  let cursor = x;
  Array.from(text).forEach((ch) => {
    ctx.fillText(ch, cursor, y);
    cursor += ctx.measureText(ch).width + letterSpacing;
  });
};

const measureSpaced = (ctx, text, letterSpacing) => {
  if (!text) return 0;
  if (supportsLetterSpacing(ctx)) ctx.letterSpacing = "0px";
  return ctx.measureText(text).width + letterSpacing * Array.from(text).length;
};

const drawDecoration = (ctx, op, metrics) => {
  const thickness = Math.max(1, op.font.size / 15);
  const lines = String(op.decoration).split(/\s+/);
  ctx.save();
  ctx.fillStyle = op.color;
  lines.forEach((kind) => {
    let y = null;
    if (kind === "underline") y = op.y + Math.max(1, op.font.size * 0.09);
    if (kind === "line-through") y = op.y - metrics.ascent * 0.3;
    if (kind === "overline") y = op.y - metrics.ascent;
    if (y !== null) ctx.fillRect(op.x, y - thickness / 2, op.width, thickness);
  });
  ctx.restore();
};

/** Split `text` into plain/hit segments for a normalized query. */
export const splitSearchSegments = (text, query) => {
  const q = normalizeSearchText(query);
  if (!q || !text) return [{ text, hit: false }];
  const lower = text.toLowerCase();
  const segments = [];
  let i = 0;
  let found = lower.indexOf(q);
  while (found !== -1) {
    if (found > i) segments.push({ text: text.slice(i, found), hit: false });
    segments.push({ text: text.slice(found, found + q.length), hit: true });
    i = found + q.length;
    found = lower.indexOf(q, i);
  }
  if (i < text.length) segments.push({ text: text.slice(i), hit: false });
  return segments;
};

const drawTextOp = (ctx, op, query) => {
  if (isInvisible(op.color) || !op.text) return;
  applyCanvasFont(ctx, op.font);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  const metrics = fontMetrics(ctx, op);
  const ls = op.font.letterSpacing || 0;
  const segments = query ? splitSearchSegments(op.text, query) : null;

  if (!segments || segments.length === 1) {
    ctx.fillStyle = op.color;
    fillTextSpaced(ctx, op.text, op.x, op.y, ls);
  } else {
    let x = op.x;
    const pad = op.font.size * 0.08;
    segments.forEach((segment) => {
      const w = measureSpaced(ctx, segment.text, ls);
      if (segment.hit) {
        ctx.fillStyle = SEARCH_HIT_BG;
        ctx.fillRect(
          x - pad,
          op.y - metrics.ascent,
          w + pad * 2,
          metrics.ascent + metrics.descent,
        );
      }
      ctx.fillStyle = segment.hit ? SEARCH_HIT_FG : op.color;
      fillTextSpaced(ctx, segment.text, x, op.y, ls);
      x += w;
    });
  }
  if (op.decoration) drawDecoration(ctx, op, metrics);
};

const drawImageOp = (ctx, op, images) => {
  const entry = images?.get?.(op.src);
  if (!entry?.image) return;
  ctx.drawImage(entry.image, op.x, op.y, op.width, op.height);
};

const roundedRectPath = (ctx, x, y, w, h, r) => {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

const drawFocusWash = (ctx, rect, color) => {
  ctx.save();
  ctx.fillStyle = color;
  roundedRectPath(ctx, rect.x, rect.y, rect.width, rect.height, FOCUS_RADIUS);
  ctx.fill();
  ctx.restore();
};

// `.focusDiv::after`: inset 1px 3px, 2px dashed border.
const drawFocusBorder = (ctx, rect, color) => {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 4]);
  roundedRectPath(
    ctx,
    rect.x + 3 + 1,
    rect.y + 1 + 1,
    rect.width - 8,
    rect.height - 4,
    FOCUS_RADIUS - 1,
  );
  ctx.stroke();
  ctx.restore();
};

/** Paint one wrapper's ops (canonical coordinates, caller sets transform). */
export function paintWrapperContent(ctx, wrapper, { yFrom = -Infinity, yTo = Infinity, images, query } = {}) {
  wrapper.credits.forEach((credit) => {
    credit.ops.forEach((op) => {
      if (!intersects(op.top, op.bottom, yFrom, yTo)) return;
      if (op.kind === "text") drawTextOp(ctx, op, query);
      else if (op.kind === "image") drawImageOp(ctx, op, images);
    });
  });
}

/**
 * Paint the visible slice of a scene. The caller has already applied the
 * canonical → device transform; yFrom/yTo are canonical scene coordinates.
 *
 * opts.focus: { idx, overlayColor, borderColor } or null
 * opts.endHold: { offset, opacity } applied to the last wrapper
 */
export function paintScene(ctx, scene, opts = {}) {
  if (!scene) return;
  const { yFrom = 0, yTo = scene.height, images, focus, endHold, query } = opts;
  const lastIndex = scene.wrappers.length - 1;

  scene.wrappers.forEach((wrapper, index) => {
    const isHeld = index === lastIndex && endHold;
    const offset = isHeld ? Number(endHold.offset) || 0 : 0;
    const top = wrapper.top + offset;
    if (!intersects(top, top + wrapper.height, yFrom, yTo)) return;

    ctx.save();
    if (isHeld) {
      ctx.translate(0, offset);
      ctx.globalAlpha *= Math.max(0, Math.min(1, Number(endHold.opacity ?? 1)));
    }
    const focused = focus
      ? wrapper.credits.find((credit) => credit.idx === focus.idx)
      : null;
    if (focused) drawFocusWash(ctx, focused.focus, focus.overlayColor);
    paintWrapperContent(ctx, wrapper, {
      yFrom: yFrom - offset,
      yTo: yTo - offset,
      images,
      query,
    });
    if (focused) drawFocusBorder(ctx, focused.focus, focus.borderColor);
    ctx.restore();
  });
}

const drawContain = (ctx, image, box) => {
  const iw = image.naturalWidth || image.width;
  const ih = image.naturalHeight || image.height;
  if (!(iw > 0) || !(ih > 0)) return;
  const scale = Math.min(box.width / iw, box.height / ih);
  const w = iw * scale;
  const h = ih * scale;
  ctx.drawImage(image, box.x + (box.width - w) / 2, box.y + (box.height - h) / 2, w, h);
};

/** `.watermark`: centered 100% × 50% box, background-size contain, 20%. */
export function paintWatermark(ctx, image, { width, height }) {
  if (!image) return;
  ctx.save();
  ctx.globalAlpha *= WATERMARK_OPACITY;
  drawContain(ctx, image, { x: 0, y: height / 4, width, height: height / 2 });
  ctx.restore();
}

/**
 * Background behind a strip of the scene, in canonical units starting at
 * scene y `stripTop`. An image background is drawn at canonical width so
 * consecutive strips form one continuous image (matching
 * getScrollBlockBackgroundStyle).
 */
export function paintStripBackground(ctx, { color, image, width, height, stripTop = 0 }) {
  if (color && color !== "transparent") {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, width, height);
  }
  if (image) {
    const iw = image.naturalWidth || image.width;
    const ih = image.naturalHeight || image.height;
    if (iw > 0 && ih > 0) {
      const drawHeight = (width * ih) / iw;
      ctx.drawImage(image, 0, -stripTop, width, drawHeight);
    }
  }
}
